import { cx } from "../../lib/cx.js";
import { Icon } from "../Icon/Icon.js";
import { VisuallyHidden } from "../VisuallyHidden/VisuallyHidden.js";
import { Button } from "../Button/Button.js";
import { Alert } from "../Alert/Alert.js";
import { Modal } from "../Modal/Modal.js";
/* empty css               */
import { useId, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
//#region src/components/UserNotice/UserNotice.tsx
var ACK_ERROR = "Couldn’t save your acknowledgement. Try again.";
/** A failed acknowledgement, under the notice's body: announced, with an icon and an "Error:" prefix. */
function AckError({ children }) {
	return /* @__PURE__ */ jsxs("p", {
		role: "alert",
		className: "ds-notice-banner__error",
		children: [/* @__PURE__ */ jsx("span", {
			className: "ds-notice-banner__error-icon",
			"aria-hidden": "true",
			children: /* @__PURE__ */ jsx(Icon, { name: "alert-circle" })
		}), /* @__PURE__ */ jsxs("span", { children: [/* @__PURE__ */ jsx(VisuallyHidden, { children: "Error: " }), children] })]
	});
}
/**
* Site notices for the signed-in user, in AppShell's banner slot: a region named
* "Notices" with an Alert per notice, newest first. A notice that requires it has an
* Acknowledge button; others can be dismissed for the session. Past two, the rest
* collapse behind "Show n more notices".
*/
function NoticeBanner({ notices, onAcknowledge, onDismiss, acknowledging = null, acknowledgeError = null, acknowledgeErrorMessage = ACK_ERROR, maxVisible = 2, acknowledgeLabel = "Acknowledge", "aria-label": ariaLabel = "Notices", className, ...rest }) {
	const id = useId();
	const [expanded, setExpanded] = useState(false);
	if (notices.length === 0) return null;
	const hidden = Math.max(0, notices.length - maxVisible);
	const shown = expanded ? notices : notices.slice(0, maxVisible);
	return /* @__PURE__ */ jsxs("section", {
		...rest,
		"aria-label": ariaLabel,
		className: cx("ds-notice-banner", className),
		children: [/* @__PURE__ */ jsx("ul", {
			id: `${id}list`,
			className: "ds-notice-banner__list",
			children: shown.map((notice) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Alert, {
				tone: notice.tone,
				title: notice.title,
				role: notice.tone === "danger" ? "alert" : "status",
				...notice.requiresAck ? { action: /* @__PURE__ */ jsx(Button, {
					size: "sm",
					variant: "secondary",
					loading: acknowledging === notice.id,
					onClick: () => onAcknowledge(notice.id),
					children: acknowledgeLabel
				}) } : {
					onDismiss: () => onDismiss(notice.id),
					dismissLabel: "Dismiss notice"
				},
				children: notice.requiresAck && acknowledgeError === notice.id ? /* @__PURE__ */ jsxs(Fragment$1, { children: [notice.body, /* @__PURE__ */ jsx(AckError, { children: acknowledgeErrorMessage })] }) : notice.body
			}) }, notice.id))
		}), hidden > 0 && /* @__PURE__ */ jsx("button", {
			type: "button",
			className: "ds-notice-banner__toggle",
			"aria-expanded": expanded,
			"aria-controls": `${id}list`,
			onClick: () => setExpanded(!expanded),
			children: expanded ? "Show fewer notices" : `Show ${hidden} more ${hidden === 1 ? "notice" : "notices"}`
		})]
	});
}
/**
* A notice that must be acknowledged before the user carries on (the legacy Pending Pay
* Notice). A Modal with no close button that Escape can't close; its only action is
* "I acknowledge". Use it sparingly.
*/
function NoticeModal({ notice, onAcknowledge, busy = false, error, acknowledgeLabel = "I acknowledge" }) {
	return /* @__PURE__ */ jsx(Modal, {
		open: notice !== null,
		onClose: () => {},
		dismissable: false,
		title: notice?.title,
		className: cx("ds-notice-modal", notice && `ds-notice-modal--${notice.tone}`),
		footer: /* @__PURE__ */ jsx(Button, {
			loading: busy,
			onClick: () => notice && onAcknowledge(notice.id),
			children: acknowledgeLabel
		}),
		children: error ? /* @__PURE__ */ jsxs("div", {
			className: "ds-notice-modal__body",
			children: [notice?.body, /* @__PURE__ */ jsx(Alert, {
				tone: "danger",
				children: error
			})]
		}) : notice?.body
	});
}
//#endregion
export { NoticeBanner, NoticeModal };

//# sourceMappingURL=UserNotice.js.map